import React, { useState } from 'react';
import { formatDatePSTShort } from '../../utils/dateUtils';
import './BillingDashboard.css';

interface InvoiceCardProps {
  invoice: {
    id: string;
    invoice_number: string;
    invoice_date: string;
    due_date: string;
    total: number;
    balance_due: number;
    status: string;
    student_id: string;
    student_name?: string;
    parent_name?: string;
  };
  onView: (invoiceId: string) => void;
  onPay?: (invoiceId: string) => void;
  onDownload?: (invoiceId: string) => void;
  onDelete?: (invoiceId: string) => void;
  onStatusChange?: (invoiceId: string, newStatus: string) => void;
  isAdmin?: boolean;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, onView, onPay, onDownload, onDelete, onStatusChange, isAdmin }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const getStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      'paid': 'status-badge status-paid',
      'partially_paid': 'status-badge status-partially-paid',
      'sent': 'status-badge status-sent',
      'overdue': 'status-badge status-overdue',
      'draft': 'status-badge status-draft',
      'cancelled': 'status-badge status-cancelled'
    };
    
    const statusLabels: { [key: string]: string } = {
      'paid': 'Paid',
      'partially_paid': 'Partially Paid',
      'sent': 'Sent',
      'overdue': 'Overdue',
      'draft': 'Draft',
      'cancelled': 'Cancelled'
    };
    
    return (
      <span className={statusClasses[status] || 'status-badge'}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const isOverdue = invoice.status === 'overdue' || 
    (invoice.status === 'sent' && new Date(invoice.due_date) < new Date());

  return (
    <div className={`invoice-card ${isOverdue ? 'overdue' : ''}`}>
      <div className="invoice-card-header">
        <div className="invoice-card-title">
          <h3>{invoice.invoice_number}</h3>
          {getStatusBadge(invoice.status)}
        </div>
        <div className="invoice-card-amount">
          <span className="amount-label">Total</span>
          <span className="amount-value">${Number(invoice.total).toFixed(2)}</span>
        </div>
      </div>
      
      <div className="invoice-card-body">
        <div className="invoice-card-info">
          <div className="info-row">
            <span className="info-label">Date:</span>
            <span className="info-value">{formatDatePSTShort(invoice.invoice_date)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Due Date:</span>
            <span className={`info-value ${isOverdue ? 'overdue-date' : ''}`}>
              {formatDatePSTShort(invoice.due_date)}
            </span>
          </div>
          {invoice.student_name && (
            <div className="info-row">
              <span className="info-label">Student:</span>
              <span className="info-value">{invoice.student_name}</span>
            </div>
          )}
          {invoice.parent_name && (
            <div className="info-row">
              <span className="info-label">Parent:</span>
              <span className="info-value">{invoice.parent_name}</span>
            </div>
          )}
          {invoice.balance_due > 0 && (
            <div className="info-row">
              <span className="info-label">Balance Due:</span>
              <span className="info-value balance-due">${Number(invoice.balance_due).toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="invoice-card-actions">
        <div className="invoice-actions-left">
          <button 
            className="btn-icon" 
            onClick={() => onView(invoice.id)}
            title="View Invoice"
          >
            👁️
          </button>
          {onDownload && (
            <button 
              className="btn-icon" 
              onClick={() => onDownload(invoice.id)}
              title="Download PDF"
            >
              ⬇️
            </button>
          )}
          {invoice.balance_due > 0 && onPay && (
            isAdmin ? (
              <button 
                className="btn-icon btn-icon-primary" 
                onClick={() => onPay(invoice.id)}
                title="Pay Now"
              >
                💳
              </button>
            ) : (
              <button 
                className="btn btn-primary btn-sm" 
                onClick={() => onPay(invoice.id)}
              >
                Pay Now
              </button>
            )
          )}
        </div>
        
        <div className="invoice-actions-right">
          {isAdmin && onStatusChange && (
            <div className="status-menu-container">
              <button 
                className="btn-icon btn-icon-status"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStatusMenu(!showStatusMenu);
                }}
                title="Change Status"
              >
                {getStatusBadge(invoice.status)}
                <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>▼</span>
              </button>
              {showStatusMenu && (
                <>
                  <div 
                    className="status-menu-overlay"
                    onClick={() => setShowStatusMenu(false)}
                  />
                  <div className="status-menu">
                  <div 
                    className={`status-menu-item ${invoice.status === 'draft' ? 'active' : ''}`}
                    onClick={() => {
                      onStatusChange(invoice.id, 'draft');
                      setShowStatusMenu(false);
                    }}
                  >
                    Draft
                  </div>
                  <div 
                    className={`status-menu-item ${invoice.status === 'sent' ? 'active' : ''}`}
                    onClick={() => {
                      onStatusChange(invoice.id, 'sent');
                      setShowStatusMenu(false);
                    }}
                  >
                    Sent
                  </div>
                  <div 
                    className={`status-menu-item ${invoice.status === 'partially_paid' ? 'active' : ''}`}
                    onClick={() => {
                      onStatusChange(invoice.id, 'partially_paid');
                      setShowStatusMenu(false);
                    }}
                  >
                    Partially Paid
                  </div>
                  <div 
                    className={`status-menu-item ${invoice.status === 'paid' ? 'active' : ''}`}
                    onClick={() => {
                      onStatusChange(invoice.id, 'paid');
                      setShowStatusMenu(false);
                    }}
                  >
                    Paid
                  </div>
                  <div 
                    className={`status-menu-item ${invoice.status === 'overdue' ? 'active' : ''}`}
                    onClick={() => {
                      onStatusChange(invoice.id, 'overdue');
                      setShowStatusMenu(false);
                    }}
                  >
                    Overdue
                  </div>
                  <div 
                    className={`status-menu-item ${invoice.status === 'cancelled' ? 'active' : ''}`}
                    onClick={() => {
                      onStatusChange(invoice.id, 'cancelled');
                      setShowStatusMenu(false);
                    }}
                  >
                    Cancelled
                  </div>
                  </div>
                </>
              )}
            </div>
          )}
          {isAdmin && onDelete && (
            <button 
              className="btn-icon btn-icon-danger" 
              onClick={() => onDelete(invoice.id)}
              title={invoice.status === 'paid' 
                ? "⚠️ WARNING: This is a paid invoice. Deleting will remove payment history." 
                : "Permanently delete this invoice"}
            >
              🗑️
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceCard;




